import { useMemo } from 'react';
import {
  NoShowRateByType,
  NoShowRateRespParams,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { AreaChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { UseRoiRequestBaseProps, useRoiRequestPayload } from '../../hooks';

interface Props extends UseRoiRequestBaseProps {
  additionalRequestPayload: {
    roiType: RoiType;
    noShowRateType: NoShowRateByType;
  };
}

interface UseFetchNSRROITimeline {
  isLoading?: boolean;
  chartData?: AreaChartData;
  rawData?: AnalyticsGatewayV1.Types.GetNoShowRateIO['output'];
}

const getValuePair = (params?: NoShowRateRespParams[]) => {
  return (
    params?.reduce<Record<string, number>>((acc, { name = '', value = 0 }) => ({ ...acc, [name]: value }), {}) || {}
  );
};

export const useFetchNSRROITimeline = (props: Props): UseFetchNSRROITimeline => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...props,
    isDemoAccount,
  });

  const { data: noShowRateResponse, isLoading } = AnalyticsGatewayV1.Queries.useGetNoShowRateQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the no-show rate details. Please try again."));
      },
    },
  });

  const chartData = useMemo(() => {
    const chartData: UseFetchNSRROITimeline['chartData'] = {
      groups: [],
    };

    // TODO :: add demo data
    const dataToUse = noShowRateResponse; // isDemoAccount ? generateNoShowRateTimelineDemoData() : noShowRateTimeline;

    if (!dataToUse) {
      return chartData;
    }

    const data = dataToUse.data;

    if (props.additionalRequestPayload?.noShowRateType === NoShowRateByType.WEAVE_AVERAGE) {
      chartData.groups =
        data?.dates?.map(({ key = '', params = [] }) => {
          const valuePair = getValuePair(params);

          return {
            name: key,
            values: {
              // These namings are defined in the backend
              practice: valuePair['SelectedLocations'] ?? 0,
              benchmark: valuePair['AllLocations'] ?? 0,
            },
          };
        }) || [];
    } else {
      chartData.groups =
        data?.dates?.map(({ key = '', params = [] }) => {
          const valuePair = getValuePair(params);

          return {
            name: key,
            values: {
              // These namings are defined in the backend
              newPatients: valuePair['New Appt'] ?? 0,
              existingPatients: valuePair['Follow Up Appt'] ?? 0,
            },
          };
        }) || [];
    }

    return chartData;
  }, [noShowRateResponse, props.additionalRequestPayload?.noShowRateType]);

  return {
    isLoading,
    chartData,
    rawData: noShowRateResponse,
  };
};
